import * as React from "react";
import HeaderBar from "../commons/components/HeaderBar";
import AsyncAction from "../AsyncAction";
import Category, {
  byOrder,
  categoryCriteria,
  getI18NKey as getCategoryI18NKey,
} from "../commons/model/treatment/Category";
import Criterion, { getI18NKey as getCriterionI18Key } from "../commons/model/treatment/Criterion";
import sfetch from "../commons/utils/super-fetch";
import "./CriteriaConfigurer.scss";
import ViewAction from "../commons/components/action/ViewAction";
import HideAction from "../commons/components/action/HideAction";
import { AvailableEntitiesSettings } from "../commons/model/EntitySettings";
import LoadingSpinner from "../flow/utils/LoadingSpinner";
import { createRoot } from "react-dom/client";

interface CriteriaConfigurerProps {
  entities: AvailableEntitiesSettings;
  configuration: CriteriaConfiguration;
  updateUrl?: string;
}

interface CriteriaConfiguration {
  criteria: Criterion[];
  disabledCriteria: Criterion[];
}

interface CriteriaConfigurerState {
  filter?: string;
  disabledCriteria: Set<Criterion>;
  editing: boolean;
  loading: boolean;
}

export default class CriteriaConfigurer extends React.PureComponent<
  CriteriaConfigurerProps,
  CriteriaConfigurerState
> {
  state: CriteriaConfigurerState = {
    disabledCriteria: this.resetConfigurationState(),
    editing: false,
    loading: false,
  };

  render() {
    const criteriaToDisplay = this.props.configuration.criteria.filter(
      (it) =>
        !this.state.filter ||
        i18n[getCriterionI18Key(it)].toLowerCase().includes(this.state.filter.toLowerCase())
    );
    const categories: { [key: string]: Criterion[] } = criteriaToDisplay.reduce(
      (acc, criterion) => {
        const category = categoryCriteria[criterion];
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(criterion);
        return acc;
      },
      {}
    );

    if (this.state.loading) {
      return <LoadingSpinner />;
    }

    return (
      <div className={`criteria-configurer${this.state.editing ? " edition" : ""}`}>
        <HeaderBar
          title={i18n["Page.CriterionConfigurer.Title"]}
          action={
            this.props.updateUrl &&
            (this.state.editing ? (
              <div className="with-margins">
                <button
                  className="button-secondary cancel"
                  onClick={() =>
                    this.setState({
                      editing: false,
                      disabledCriteria: this.resetConfigurationState(),
                    })
                  }
                >
                  {i18n["Global.Cancel"]}
                </button>
                <AsyncAction
                  buttonLabel={i18n["Global.Save"]}
                  promise={() => this.persistChanges()}
                  onSuccess={() => document.location.reload()}
                />
              </div>
            ) : (
              <button className="button-primary" onClick={() => this.setState({ editing: true })}>
                <i className="ic_edit" />
                <span>{i18n["Global.Edit"]}</span>
              </button>
            ))
          }
          searchConfiguration={{
            value: this.state.filter,
            onChange: (filter) => this.setState({ filter }),
            placeholder: i18n["Page.CriterionConfigurer.Filter.Placeholder"],
          }}
          entities={this.props.entities}
          onChange={() => {
            this.setState({ loading: true });
          }}
        />
        <div className="container">
          {Object.keys(categories)
            .sort(byOrder)
            .map((category: Category) => (
              <div key={category} className="category panel with-shadow">
                <h1
                  className={`title${
                    this.isCategoryDisabled(categories[category]) ? " disabled" : ""
                  }`}
                >
                  <span>{i18n[getCategoryI18NKey(category)]}</span>
                  {this.isCategoryDisabled(categories[category]) ? (
                    <ViewAction onClick={() => this.setCategoryState(category, false)} />
                  ) : (
                    <HideAction onClick={() => this.setCategoryState(category, true)} />
                  )}
                </h1>
                <div className="grid">
                  {categories[category].map((criterion) => (
                    <div
                      key={criterion}
                      className={`criterion-card${
                        this.state.disabledCriteria.has(criterion) ? " disabled" : ""
                      }`}
                    >
                      {i18n[getCriterionI18Key(criterion)]}
                      {this.state.disabledCriteria.has(criterion) ? (
                        <ViewAction onClick={() => this.setCriterionState(criterion, false)} />
                      ) : (
                        <HideAction onClick={() => this.setCriterionState(criterion, true)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }

  resetConfigurationState() {
    return new Set(this.props.configuration.disabledCriteria);
  }

  isCategoryDisabled(categoryCriteria: Criterion[]) {
    return categoryCriteria.every((criterion) => this.state.disabledCriteria.has(criterion));
  }

  setCategoryState(category: string, disable: boolean) {
    if (this.state.editing) {
      const disabledCriteria = new Set(this.state.disabledCriteria);
      this.props.configuration.criteria
        .filter((it) => categoryCriteria[it] === category)
        .forEach((it) => this.setCriterion(disabledCriteria, it, disable));
      this.setState({ disabledCriteria });
    }
  }

  setCriterionState(criterion: Criterion, disable: boolean) {
    if (this.state.editing) {
      const disabledCriteria = new Set(this.state.disabledCriteria);
      this.setCriterion(disabledCriteria, criterion, disable);
      this.setState({ disabledCriteria });
    }
  }

  setCriterion(disabledCriteria: Set<Criterion>, criterion: Criterion, disable: boolean) {
    if (disable) {
      disabledCriteria.add(criterion);
    } else {
      disabledCriteria.delete(criterion);
    }
  }

  persistChanges() {
    return sfetch(this.props.updateUrl, {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([...this.state.disabledCriteria]),
    });
  }

  static loadComponent(elementId: string, props: CriteriaConfigurerProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<CriteriaConfigurer {...props} />);
  }
}
